import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import QRCode from 'qrcode';

// Dark Reader's ONLY officially-supported, reliable opt-out is a
// PAGE-LEVEL <meta name="darkreader-lock"> (verified against Dark Reader
// docs + maintainer discussions: there is NO per-element, attribute,
// class, shadow-DOM, or per-iframe bypass — those were tried by others
// and do not work). So while a Player ID card is on screen we lock the
// whole page; on destroy we release it. Ref-counted so multiple cards or
// quick route re-entry don't fight over the meta tag. We only remove a
// lock element we added ourselves (never one a host page set).
let darkReaderLockRefs = 0;
let darkReaderLockEl: HTMLMetaElement | null = null;

function acquireDarkReaderLock(): void {
  darkReaderLockRefs++;
  if (darkReaderLockRefs > 1 || darkReaderLockEl) return;
  try {
    if (document.querySelector('meta[name="darkreader-lock"]')) return;
    const meta = document.createElement('meta');
    meta.name = 'darkreader-lock';
    document.head.appendChild(meta);
    darkReaderLockEl = meta;
  } catch {
    /* no-op: never let theming break the card */
  }
}

function releaseDarkReaderLock(): void {
  if (darkReaderLockRefs > 0) darkReaderLockRefs--;
  if (darkReaderLockRefs > 0) return;
  try {
    darkReaderLockEl?.parentNode?.removeChild(darkReaderLockEl);
  } catch {
    /* no-op */
  }
  darkReaderLockEl = null;
}

@Component({
  selector: 'app-player-id-card',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './player-id-card.component.html',
  styleUrl: './player-id-card.component.scss',
})
export class PlayerIdCardComponent
  implements OnChanges, OnInit, OnDestroy
{
  @Input() userId = '';
  @Input() userName = '';

  qrDataUrl = signal<string | null>(null);
  shortId = signal<string>('');
  loading = signal(true);

  ngOnInit(): void {
    // Disable Dark Reader for the page while the Player ID is shown — the
    // only documented, reliable way to keep the card exactly as authored.
    acquireDarkReaderLock();
  }

  ngOnDestroy(): void {
    releaseDarkReaderLock();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['userId'] || changes['userName']) && this.userId) {
      this.generateQrCode();
    }
  }

  private async generateQrCode(): Promise<void> {
    this.loading.set(true);

    try {
      // The QR carries the raw user_id. The entrance scanner reads it
      // directly and the club gates on the member's subscription /
      // verification (see gym-check-in), so the code is club-agnostic — one
      // card works at any club's door.
      const value = this.userId;
      this.shortId.set(value.replace(/-/g, '').slice(-8).toUpperCase());

      const dataUrl = await QRCode.toDataURL(value, {
        width: 360,
        margin: 0,
        color: {
          dark: '#1a1a2e',
          light: '#ffffff',
        },
        errorCorrectionLevel: 'M',
      });

      this.qrDataUrl.set(dataUrl);
    } catch (error) {
      console.error('Failed to generate Player ID QR code:', error);
      this.qrDataUrl.set(null);
    } finally {
      this.loading.set(false);
    }
  }
}
